import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faAppleAlt, faExclamationTriangle, faChevronDown, faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { BackendChangeIndicator } from '../common/BackendChangeIndicator';

// Hilfsfunktion für einheitliche "Kein Wert" Anzeige
const renderEmptyValue = (value: any, fallback = 'Kein Wert') => {
  if (!value || value === '' || value === null || value === undefined) {
    return <span className="text-xs text-gray-500 italic">{fallback}</span>;
  }
  return value;
};

// Austrian/German LMIV Allergens
const ALLERGENS = [
  { key: 'gluten', label: 'Glutenhaltiges Getreide', description: 'Weizen, Roggen, Gerste, Hafer, Dinkel, Kamut' },
  { key: 'crustaceans', label: 'Krebstiere', description: 'Garnelen, Krabben, Hummer, Langusten' },
  { key: 'eggs', label: 'Eier', description: 'Hühnereier und Erzeugnisse daraus' },
  { key: 'fish', label: 'Fisch', description: 'Fisch und Erzeugnisse daraus' },
  { key: 'peanuts', label: 'Erdnüsse', description: 'Erdnüsse und Erzeugnisse daraus' },
  { key: 'soybeans', label: 'Sojabohnen', description: 'Soja und Erzeugnisse daraus' },
  { key: 'milk', label: 'Milch/Laktose', description: 'Milch und Erzeugnisse daraus (einschließlich Laktose)' },
  { key: 'nuts', label: 'Schalenfrüchte', description: 'Mandeln, Haselnüsse, Walnüsse, Cashew, Pekan, Paranüsse, Pistazien, Macadamia' },
  { key: 'celery', label: 'Sellerie', description: 'Sellerie und Erzeugnisse daraus' },
  { key: 'mustard', label: 'Senf', description: 'Senf und Erzeugnisse daraus' },
  { key: 'sesame', label: 'Sesamsamen', description: 'Sesamsamen und Erzeugnisse daraus' },
  { key: 'sulphites', label: 'Schwefeldioxid/Sulphite', description: 'Schwefeldioxid und Sulphite in Konzentrationen von mehr als 10 mg/kg oder 10 mg/l' },
  { key: 'lupin', label: 'Lupinen', description: 'Lupinen und Erzeugnisse daraus' },
  { key: 'molluscs', label: 'Weichtiere', description: 'Weichtiere und Erzeugnisse daraus' }
];

// LMIV Nutrition Values with tolerances - Keys match backend structure
const NUTRITION_VALUES = [
  { key: 'energy.kj', label: 'Brennwert', unit: 'kJ', tolerance: '±8%', required: true, decimals: 0 },
  { key: 'energy.kcal', label: 'Brennwert', unit: 'kcal', tolerance: '±8%', required: true, decimals: 0 },
  { key: 'fat.total', label: 'Fett', unit: 'g', tolerance: '<10g: ±1,5g | 10-40g: ±20% | >40g: ±8g', required: true, decimals: 1 },
  { key: 'fat.saturated', label: '- davon gesättigte Fettsäuren', unit: 'g', tolerance: '<4g: ±0,8g | ≥4g: ±20%', required: true, decimals: 1 },
  { key: 'carbohydrates.total', label: 'Kohlenhydrate', unit: 'g', tolerance: '<10g: ±2g | 10-40g: ±20% | >40g: ±8g', required: true, decimals: 1 },
  { key: 'carbohydrates.sugars', label: '- davon Zucker', unit: 'g', tolerance: '<5g: ±1,5g | 5-10g: ±30% | >10g: ±3g', required: true, decimals: 1 },
  { key: 'fiber', label: 'Ballaststoffe', unit: 'g', tolerance: '<3g: ±1g | 3-6g: ±33% | >6g: ±2g', required: false, decimals: 1 },
  { key: 'protein', label: 'Eiweiß', unit: 'g', tolerance: '<5g: ±1,5g | 5-10g: ±30% | >10g: ±3g', required: true, decimals: 1 },
  { key: 'salt', label: 'Salz', unit: 'g', tolerance: '<0,5g: ±0,15g | 0,5-1,25g: ±30% | >1,25g: ±0,375g', required: true, decimals: 2 },
  { key: 'sodium', label: 'Natrium', unit: 'mg', tolerance: 'Berechnet aus Salzwert ÷ 2,5', required: false, decimals: 0 },
  { key: 'vitaminA', label: 'Vitamin A', unit: 'μg', tolerance: '±20%', required: false, decimals: 1 },
  { key: 'vitaminD', label: 'Vitamin D', unit: 'μg', tolerance: '±20%', required: false, decimals: 1 },
  { key: 'vitaminE', label: 'Vitamin E', unit: 'mg', tolerance: '±20%', required: false, decimals: 1 },
  { key: 'vitaminK', label: 'Vitamin K', unit: 'μg', tolerance: '±20%', required: false, decimals: 1 },
  { key: 'vitaminC', label: 'Vitamin C', unit: 'mg', tolerance: '±20%', required: false, decimals: 1 },
  { key: 'thiamin', label: 'Thiamin (B1)', unit: 'mg', tolerance: '±20%', required: false, decimals: 2 },
  { key: 'riboflavin', label: 'Riboflavin (B2)', unit: 'mg', tolerance: '±20%', required: false, decimals: 2 },
  { key: 'niacin', label: 'Niacin (B3)', unit: 'mg', tolerance: '±20%', required: false, decimals: 1 },
  { key: 'vitaminB6', label: 'Vitamin B6', unit: 'mg', tolerance: '±20%', required: false, decimals: 2 },
  { key: 'folate', label: 'Folsäure', unit: 'μg', tolerance: '±20%', required: false, decimals: 0 },
  { key: 'vitaminB12', label: 'Vitamin B12', unit: 'μg', tolerance: '±20%', required: false, decimals: 1 },
  { key: 'biotin', label: 'Biotin', unit: 'μg', tolerance: '±20%', required: false, decimals: 1 },
  { key: 'pantothenicAcid', label: 'Pantothensäure', unit: 'mg', tolerance: '±20%', required: false, decimals: 1 },
  { key: 'potassium', label: 'Kalium', unit: 'mg', tolerance: '±20%', required: false, decimals: 0 },
  { key: 'chloride', label: 'Chlorid', unit: 'mg', tolerance: '±20%', required: false, decimals: 0 },
  { key: 'calcium', label: 'Calcium', unit: 'mg', tolerance: '±20%', required: false, decimals: 0 },
  { key: 'phosphorus', label: 'Phosphor', unit: 'mg', tolerance: '±20%', required: false, decimals: 0 },
  { key: 'magnesium', label: 'Magnesium', unit: 'mg', tolerance: '±20%', required: false, decimals: 0 },
  { key: 'iron', label: 'Eisen', unit: 'mg', tolerance: '±20%', required: false, decimals: 1 },
  { key: 'zinc', label: 'Zink', unit: 'mg', tolerance: '±20%', required: false, decimals: 1 },
  { key: 'copper', label: 'Kupfer', unit: 'mg', tolerance: '±20%', required: false, decimals: 2 },
  { key: 'manganese', label: 'Mangan', unit: 'mg', tolerance: '±20%', required: false, decimals: 2 },
  { key: 'fluoride', label: 'Fluorid', unit: 'mg', tolerance: '±20%', required: false, decimals: 2 },
  { key: 'selenium', label: 'Selen', unit: 'μg', tolerance: '±20%', required: false, decimals: 1 },
  { key: 'chromium', label: 'Chrom', unit: 'μg', tolerance: '±20%', required: false, decimals: 1 },
  { key: 'molybdenum', label: 'Molybdän', unit: 'μg', tolerance: '±20%', required: false, decimals: 1 },
  { key: 'iodine', label: 'Jod', unit: 'μg', tolerance: '±20%', required: false, decimals: 1 }
];

// **NutritionRow Component - moved outside to prevent re-creation on each render**
interface NutritionRowProps {
  nutrition: any;
  data: any;
  mode: 'view' | 'edit';
  updateNutritionValue: (key: string, value: string) => void;
  getNutritionValue: (key: string) => any;
  renderFieldWithHistory: (fieldName: string, fieldPath: string, content: React.ReactNode) => React.ReactNode;
}

const NutritionRow: React.FC<NutritionRowProps> = React.memo(({ 
  nutrition, 
  data, 
  mode, 
  updateNutritionValue, 
  getNutritionValue, 
  renderFieldWithHistory 
}) => {
  console.log(`[NUTRITION_ROW] ${nutrition.label}:`, {
    key: nutrition.key,
    hasRenderFunction: !!renderFieldWithHistory
  });
  
  return (
    <tr className="hover:bg-gray-50">
      <td className={`border border-gray-300 px-2 py-1 font-medium text-xs ${nutrition.label.startsWith('-') ? 'pl-4 text-gray-700' : 'text-gray-900'}`}>
        {nutrition.label}
        {nutrition.required && <span className="text-red-500 ml-1">*</span>}
      </td>
      <td className="border border-gray-300 px-2 py-1">
        {renderFieldWithHistory(`Nährwert ${nutrition.label}`, `lmivData.nutritionalValues.${nutrition.key}`, (
          <>
            {mode === 'edit' ? (
              <input
                type="number"
                step={nutrition.decimals === 0 ? "1" : nutrition.decimals === 1 ? "0.1" : "0.01"}
                value={getNutritionValue(nutrition.key) || ''}
                onChange={(e) => updateNutritionValue(nutrition.key, e.target.value)}
                className="w-full border border-gray-300 rounded px-1 py-0.5 focus:ring-1 focus:ring-blue-500 text-xs h-[28px]"
                placeholder="0"
              />
            ) : (
              <span className="text-xs">
                {(() => {
                  const value = getNutritionValue(nutrition.key);
                  return value !== null && value !== undefined && value !== '' ? value : '-';
                })()}
              </span>
            )}
          </>
        ))}
      </td>
      <td className="border border-gray-300 px-2 py-1 text-xs font-medium">{nutrition.unit}</td>
      <td className="border border-gray-300 px-2 py-1 text-xs text-gray-600">
        {nutrition.tolerance}
      </td>
    </tr>
  );
});

interface ArticleDetailNaehrwerteProps {
  data: any;
  mode: 'view' | 'edit';
  onChange: (field: string, value: any) => void;
  isSupplierMode?: boolean;
  originalData?: any;
  articleNumber?: string;
}

const ArticleDetailNaehrwerte: React.FC<ArticleDetailNaehrwerteProps> = ({ 
  data, 
  mode, 
  onChange, 
  isSupplierMode = false,
  originalData,
  articleNumber
}) => {

  const renderFieldWithHistory = (
    fieldName: string, 
    fieldPath: string,
    content: React.ReactNode
  ) => {
    // Change tracking only for Admin (not Supplier mode)
    if (isSupplierMode || !articleNumber) {
      return content;
    }
    
    return (
      <BackendChangeIndicator
        fieldName={fieldName}
        fieldPath={fieldPath}
        articleNumber={articleNumber}
      >
        {content}
      </BackendChangeIndicator>
    );
  };

  const [showOptionalNutrition, setShowOptionalNutrition] = useState(false);
  const [showAllergensExpanded, setShowAllergensExpanded] = useState(false);

  const requiredNutrition = NUTRITION_VALUES.filter(n => n.required);
  const optionalNutrition = NUTRITION_VALUES.filter(n => !n.required);

  const updateNutritionValue = (key: string, value: string) => {
    const currentLmivData = data?.lmivData || {};
    const nutritionalValues = { ...currentLmivData.nutritionalValues };
    
    // Handle nested paths like "energy.kcal", "fat.total", etc.
    const keyParts = key.split('.');
    if (keyParts.length === 2) {
      const [category, subKey] = keyParts;
      if (!nutritionalValues[category]) {
        nutritionalValues[category] = {};
      }
      // Convert empty string or undefined to null, otherwise parse as float
      if (value === '' || value === undefined || value === null) {
        nutritionalValues[category][subKey] = null;
      } else {
        const parsedValue = parseFloat(value);
        nutritionalValues[category][subKey] = isNaN(parsedValue) ? null : parsedValue;
      }
    } else {
      // Simple key like "protein", "salt"
      if (value === '' || value === undefined || value === null) {
        nutritionalValues[key] = null;
      } else {
        const parsedValue = parseFloat(value);
        nutritionalValues[key] = isNaN(parsedValue) ? null : parsedValue;
      }
    }
    
    const updatedLmivData = {
      ...currentLmivData,
      nutritionalValues
    };
    
    onChange('lmivData', updatedLmivData);
  };

  // Helper function to get nested nutrition value - moved outside of component to prevent re-creation
  const getNutritionValue = (key: string) => {
    const keyParts = key.split('.');
    if (keyParts.length === 2) {
      const [category, subKey] = keyParts;
      const value = data?.lmivData?.nutritionalValues?.[category]?.[subKey];
      // Ensure we return a primitive value, not an object
      return (value !== null && value !== undefined && typeof value !== 'object') ? value : '';
    } else {
      const value = data?.lmivData?.nutritionalValues?.[key];
      // Ensure we return a primitive value, not an object
      return (value !== null && value !== undefined && typeof value !== 'object') ? value : '';
    }
  };

  const updateAllergenValue = (key: string, value: boolean) => {
    const currentLmivData = data?.lmivData || {};
    const allergens = { ...currentLmivData.allergens };
    
    // Handle nested allergen structure (e.g., "glutenhaltigesGetreide.contains")
    const [allergenKey, property] = key.split('.');
    if (!allergens[allergenKey]) {
      allergens[allergenKey] = {};
    }
    
    // Exklusive Logik: Wenn "contains" gesetzt wird, "mayContain" auf false setzen und umgekehrt
    if (property === 'contains' && value) {
      allergens[allergenKey].contains = true;
      allergens[allergenKey].mayContain = false;
    } else if (property === 'mayContain' && value) {
      allergens[allergenKey].mayContain = true;
      allergens[allergenKey].contains = false;
    } else {
      // Wenn abgewählt wird, einfach das entsprechende Feld auf false setzen
      allergens[allergenKey][property] = value;
    }
    
    const updatedLmivData = {
      ...currentLmivData,
      allergens
    };
    
    onChange('lmivData', updatedLmivData);
  };

  return (
    <div className="space-y-6">
      {/* **NÄHRWERTTABELLE LMIV** */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-2">
            <h4 className="text-lg font-semibold text-gray-900">Nährwerte pro 100g/100ml</h4>
          </div>
        </div>

        <div className="p-6">
          {/* Required Nutrition Values */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-gray-800 text-xs">Nährstoff</th>
                  <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-gray-800 text-xs">Wert</th>
                  <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-gray-800 text-xs">Einheit</th>
                  <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-gray-800 text-xs">Toleranz</th>
                </tr>
              </thead>
              <tbody>
                {requiredNutrition.map(nutrition => (
                  <NutritionRow 
                    key={nutrition.key} 
                    nutrition={nutrition}
                    data={data}
                    mode={mode}
                    updateNutritionValue={updateNutritionValue}
                    getNutritionValue={getNutritionValue}
                    renderFieldWithHistory={renderFieldWithHistory}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Optional Nutrition Values */}
          <div className="mt-4">
            <button
              onClick={() => setShowOptionalNutrition(!showOptionalNutrition)}
              className="flex items-center gap-2 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              <FontAwesomeIcon icon={showOptionalNutrition ? faChevronDown : faChevronRight} />
              Freiwillige Nährwertangaben (Vitamine & Mineralstoffe)
            </button>
            
            {showOptionalNutrition && (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-blue-50">
                      <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-gray-800 text-xs">Nährstoff</th>
                      <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-gray-800 text-xs">Wert</th>
                      <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-gray-800 text-xs">Einheit</th>
                      <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-gray-800 text-xs">Toleranz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {optionalNutrition.map(nutrition => (
                      <NutritionRow 
                        key={nutrition.key} 
                        nutrition={nutrition}
                        data={data}
                        mode={mode}
                        updateNutritionValue={updateNutritionValue}
                        getNutritionValue={getNutritionValue}
                        renderFieldWithHistory={renderFieldWithHistory}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* **ALLERGENE TABELLE** */}
      {renderFieldWithHistory('Allergene & Unverträglichkeiten', 'lmivData.allergens', (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3 mb-2">
              <h4 className="text-lg font-semibold text-gray-900">Allergene & Unverträglichkeiten (LMIV)</h4>
            </div>
          </div>

          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-gray-800 text-xs">Allergen</th>
                    <th className="border border-gray-300 px-2 py-2 text-center font-semibold text-gray-800 text-xs">Enthalten</th>
                    <th className="border border-gray-300 px-2 py-2 text-center font-semibold text-gray-800 text-xs">Kann enthalten</th>
                    <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-gray-800 text-xs">Beschreibung</th>
                  </tr>
                </thead>
                <tbody>
                  {ALLERGENS.map(allergen => (
                    <tr key={allergen.key} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-2 py-1 font-medium text-xs">
                        {allergen.label}
                      </td>
                      <td className="border border-gray-300 px-2 py-1 text-center">
                        {renderFieldWithHistory(`${allergen.label} enthalten`, `lmivData.allergens.${allergen.key}.contains`, (
                          <>
                            {mode === 'edit' ? (
                              <input
                                type="checkbox"
                                checked={data?.lmivData?.allergens?.[allergen.key]?.contains || false}
                                onChange={(e) => updateAllergenValue(`${allergen.key}.contains`, e.target.checked)}
                                className="w-3 h-3 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                              />
                            ) : (
                              <span className={`inline-block w-3 h-3 rounded ${data?.lmivData?.allergens?.[allergen.key]?.contains ? 'bg-red-500' : 'bg-gray-200'}`} />
                            )}
                          </>
                        ))}
                      </td>
                      <td className="border border-gray-300 px-2 py-1 text-center">
                        {renderFieldWithHistory(`${allergen.label} kann enthalten`, `lmivData.allergens.${allergen.key}.mayContain`, (
                          <>
                            {mode === 'edit' ? (
                              <input
                                type="checkbox"
                                checked={data?.lmivData?.allergens?.[allergen.key]?.mayContain || false}
                                onChange={(e) => updateAllergenValue(`${allergen.key}.mayContain`, e.target.checked)}
                                className="w-3 h-3 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                              />
                            ) : (
                              <span className={`inline-block w-3 h-3 rounded ${data?.lmivData?.allergens?.[allergen.key]?.mayContain ? 'bg-yellow-500' : 'bg-gray-200'}`} />
                            )}
                          </>
                        ))}
                      </td>
                      <td className="border border-gray-300 px-2 py-1 text-xs text-gray-600">
                        {allergen.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="mt-4 p-2 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs text-red-800 leading-tight">
                <strong>Wichtig:</strong> Allergenkennzeichnung ist nach LMIV verpflichtend. 
                "Enthalten" = Zutat oder Verarbeitungshilfsstoff. "Kann enthalten" = Kreuzkontamination möglich.
                Die Kennzeichnung muss in der Zutatenliste und bei loser Ware deutlich sichtbar erfolgen.
              </p>
            </div>
          </div>
        </div>
      ))}

      {/* **ZUTATENLISTE & LMIV-ANGABEN** */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zutatenliste <span className="text-red-500">*</span>
            </label>
            {renderFieldWithHistory(
              'Zutatenliste',
              'lmivData.ingredients',
              mode === 'edit' ? (
                <textarea
                  value={data?.lmivData?.ingredients || ''}
                  onChange={(e) => {
                    const updatedLmivData = {
                      ...data?.lmivData,
                      ingredients: e.target.value
                    };
                    onChange('lmivData', updatedLmivData);
                  }}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Zutaten in absteigender Reihenfolge ihrer Gewichtsanteile..."
                />
              ) : (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <span className="text-sm text-gray-700">
                    {renderEmptyValue(data?.lmivData?.ingredients)}
                  </span>
                </div>
              )
            )}
          </div>

          {/* Primärzutat */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primärzutat
            </label>
            {renderFieldWithHistory(
              'Primärzutat',
              'primaryIngredient',
              mode === 'edit' ? (
                <input
                  type="text"
                  value={data?.primaryIngredient || ''}
                  onChange={(e) => onChange('primaryIngredient', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Hauptzutat des Produktes..."
                />
              ) : (
                <p className="text-sm text-gray-900 p-3 bg-gray-50 rounded-lg">
                  {renderEmptyValue(data?.primaryIngredient)}
                </p>
              )
            )}
          </div>

          {/* Alkoholgehalt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Alkoholgehalt (‰)
            </label>
            {renderFieldWithHistory(
              'Alkoholgehalt',
              'alcoholContent',
              mode === 'edit' ? (
                <input
                  type="number"
                  step="0.01"
                  value={data?.alcoholContent || ''}
                  onChange={(e) => onChange('alcoholContent', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                />
              ) : (
                <p className="text-sm text-gray-900 p-3 bg-gray-50 rounded-lg">
                  {data?.alcoholContent ? `${data.alcoholContent}‰` : renderEmptyValue(null)}
                </p>
              )
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Besondere Lager-/Verwendungshinweise
            </label>
            {renderFieldWithHistory(
              'Lagerhinweise',
              'lmivData.storage',
              mode === 'edit' ? (
                <textarea
                  value={data?.lmivData?.storage || ''}
                  onChange={(e) => {
                    const updatedLmivData = {
                      ...data?.lmivData,
                      storage: e.target.value
                    };
                    onChange('lmivData', updatedLmivData);
                  }}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="z.B. Kühl und trocken lagern, Nach dem Öffnen im Kühlschrank aufbewahren..."
                />
              ) : (
                <p className="text-sm text-gray-900 p-3 bg-gray-50 rounded-lg">
                  {renderEmptyValue(data?.lmivData?.storage, 'Keine besonderen Hinweise')}
                </p>
              )
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verwendungshinweise
            </label>
            {renderFieldWithHistory(
              'Verwendungshinweise',
              'lmivData.usageInstructions',
              mode === 'edit' ? (
                <textarea
                  value={data?.lmivData?.usageInstructions || ''}
                  onChange={(e) => {
                    const updatedLmivData = {
                      ...data?.lmivData,
                      usageInstructions: e.target.value
                    };
                    onChange('lmivData', updatedLmivData);
                  }}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="z.B. Zubereitungshinweise, Portionsempfehlung..."
                />
              ) : (
                <p className="text-sm text-gray-900 p-3 bg-gray-50 rounded-lg">
                  {renderEmptyValue(data?.lmivData?.usageInstructions, 'Keine Verwendungshinweise')}
                </p>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetailNaehrwerte;
